<template>

    <v-card class="utroseno">
        <v-card-title class="naslov-kartice">
          <span class="glif">&#9660;</span> Utrošak materijala
          <span class="smjer">&minus; razdužuje skladište</span>
          <span class="znak">{{ items.length }} {{ mnozinaStavke }}</span>
          <v-spacer></v-spacer>
          <span class="iznos">{{ localMoney(zbrojSkupine) }}</span>
          <v-btn icon small color="primary" class="ml-2" @click="newMaterial" title="Dodaj stavku">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-card-title>
        <!-- Unos u retku: smjer se ne bira, odreduje ga skupina u koju se
             upisuje. Enter sprema i vraca kursor na sifru, pa se niz stavaka
             unese bez misa. Stavka ide u store, isto kao sto to radi i ekran
             za unos - na posluzitelj se salje tek kad se spremi cijeli nalog. -->
        <div class="unos" v-if="!locked">
          <v-autocomplete
            ref="sifra"
            outlined dense hide-details="auto"
            :items="sifrarnik"
            v-model="novaSifra"
            label="Šifra / naziv"
            item-text="itemByID"
            item-value="itemID"
            :filter="sveRijeci"
            return-object
            clearable
            class="polje-sifra"
            @change="artiklOdabran"
          ></v-autocomplete>
          <v-text-field outlined dense hide-details="auto" label="Naziv"
            :value="noviNaziv" readonly class="polje-naziv"></v-text-field>
          <v-text-field outlined dense hide-details="auto" label="JM"
            :value="novaMjera" readonly class="polje-jm"></v-text-field>
          <v-text-field outlined dense hide-details="auto" label="Količina"
            v-model.number="novaKolicina" type="number" class="polje-broj"
            @keyup.enter="dodaj"></v-text-field>
          <v-text-field outlined dense hide-details="auto" label="Dobavna cijena"
            v-model.number="novaCijena" type="number" class="polje-broj"
            @keyup.enter="dodaj"></v-text-field>
          <v-text-field outlined dense hide-details="auto" label="Vrijednost"
            :value="localMoney(novaVrijednost)" readonly class="polje-broj"></v-text-field>
          <div class="gumbi">
            <v-btn color="primary" small :disabled="!smijeDodati" @click="dodaj">
              {{ uredujem ? 'Spremi' : 'Dodaj' }}
            </v-btn>
            <v-btn text small v-if="uredujem" @click="odustani">Odustani</v-btn>
          </div>
        </div>
        <div class="greska" v-if="greske.length">{{ greske.join(' ') }}</div>

        <v-data-table
            :headers="headers"
            :items="items"
            :search="search"
            hide-default-footer
            :items-per-page="-1"
            item-key="id"
            dense
            :mobile-breakpoint="0"
            @click:row="navigateTo"
          >
          <!-- Kolicina je pozitivan broj. Smjer nosi "documentSide" = 2, a na
               ekranu ga kaze naslov sekcije - predznak bi bio samo buka, i k
               tome kriv na stornu, gdje je kolicina vec negativna. -->
          <template v-slot:[`item.itemPcsMinus`]="{ item }">
            <span class="kol">{{ localMoney(item.itemPcsMinus) }}</span>
          </template>
          <template v-slot:[`item.itemSuppBasePrice`]="{ item }">
            {{ localMoney(item.itemSuppBasePrice) }}
          </template>
          <template v-slot:[`item.itemBaseAmount`]="{ item }">
            {{ localMoney(item.itemBaseAmount) }}
          </template>
          <!-- Zbroj stoji i na dnu tablice, ne samo u naslovu: kod duljeg
               popisa naslov odskrola gore. -->
          <template v-slot:[`body.append`]>
            <tr class="zbroj-red">
              <td :colspan="3"></td>
              <td class="text-end">Ukupno</td>
              <td></td>
              <td class="text-end">{{ localMoney(zbrojSkupine) }}</td>
              <td></td>
            </tr>
          </template>
          <template v-slot:[`item.obrisi`]="{ item }">
            <v-icon small color="red darken-1" @click.stop="obrisi(item)"
                    title="Obriši stavku">mdi-delete-outline</v-icon>
          </template>
          <template v-slot:no-data>Materijal nije upisan.</template>
        </v-data-table>
      </v-card>

</template>

<script>
// import axios from 'axios'
import moneyFormat from '@/snippets/moneyFormat'
import { sveRijeci } from '@/snippets/tekst'
import { slozi, provjeri, zbrojNaloga, MATERIJAL } from './radniNalogZapis'

export default {
  computed: {
    sifrarnik () {
      return this.$store.state.itemStore ? this.$store.state.itemStore : []
    },
    noviNaziv () { return this.noviArtikl ? this.noviArtikl.itemName : '' },
    novaMjera () { return this.noviArtikl ? this.noviArtikl.itemUnit : '' },
    novaVrijednost () {
      return Math.round((parseFloat(this.novaKolicina) || 0) * (parseFloat(this.novaCijena) || 0) * 100) / 100
    },
    uredujem () { return this.urediIndex >= 0 },
    smijeDodati () {
      return !!(this.noviArtikl && this.noviArtikl.itemID) && (parseFloat(this.novaKolicina) || 0) !== 0
    },
    // Jezik se cita jednom, a ne u svakom stupcu posebno.
    en () {
      return this.$store.state.language === 'EN'
    },
    naziviStupaca () {
      const en = this.$store.state.language === 'EN'
      return {
        sifra: en ? 'Code' : 'Šifra',
        naziv: en ? 'Name' : 'Naziv',
        jm: en ? 'Unit' : 'JM',
        kolicina: en ? 'Quantity' : 'Količina',
        cijena: en ? 'Supp. price' : 'Dobavna cijena',
        vrijednost: en ? 'Value' : 'Vrijednost'
      }
    },
    // Zbroj skupine stoji u naslovu - prije se vidio tek u financijskoj
    // kartici, nakon sto se prolista cijela tablica.
    zbrojSkupine () {
      return (this.items || []).reduce((z, s) => z + (parseFloat(s.itemBaseAmount) || 0), 0)
    },
    mnozinaStavke () {
      const n = (this.items || []).length
      return n === 1 ? 'stavka' : (n >= 2 && n <= 4 ? 'stavke' : 'stavaka')
    }
  },
  name: 'documentdetaillist3',
  data () {
    return {
      msg: '',
      items: [],
      locked: false,
      novaSifra: null,
      noviArtikl: null,
      novaKolicina: null,
      novaCijena: null,
      greske: [],
      urediIndex: -1,
      max25chars: (v) => v.length <= 25 || 'Input too long!',
      tmp: '',
      search: '',
      pagination: {},
      rowsPerPageItems: [50],
      headers: [ ],
      mainQuery: {
        CompanyId: 1,
        businessYear: 0,
        documentName: '',
        databaseDocID: 0,
        FreeF1: 1
      }
    }
  },
  async mounted () {
    try {
      if (this.$store.state.companyid) {
        this.mainQuery.CompanyId = this.$store.state.companyid
      }
      if (this.$store.state.businessYear) {
        this.mainQuery.businessYear = this.$store.state.businessYear
      }
      if (this.$store.state.documentActiv) {
        this.mainQuery.documentName = this.$store.state.documentActiv
      }
      if (this.$store.state.databaseDocID) {
        this.mainQuery.databaseDocID = this.$store.state.databaseDocID
      }
      this.locked = this.$store.state.documentActivHead &&
        this.$store.state.documentActivHead.documentLocked === true
      this.mainQuery.FreeF1 = 1
      this.items = this.$store.state.documentActivItems.filter(item => {
            return parseInt(item.FreeF6)  === 3
          })
 
      // Stupci su isti na oba jezika: sifra i naziv odvojeni, brojke desno,
      // interni "Id baze" maknut s ekrana. Kolicina nosi predznak minus, jer
      // materijal razduzuje skladiste.
      this.headers = [
        { text: this.naziviStupaca.sifra, align: 'start', sortable: true, value: 'itemID', width: '110px' },
        { text: this.naziviStupaca.naziv, align: 'start', sortable: true, value: 'itemName' },
        { text: this.naziviStupaca.jm, align: 'start', value: 'itemUnit', width: '60px' },
        { text: this.naziviStupaca.kolicina, align: 'end', value: 'itemPcsMinus', width: '130px' },
        { text: this.naziviStupaca.cijena, align: 'end', value: 'itemSuppBasePrice', width: '130px' },
        { text: this.naziviStupaca.vrijednost, align: 'end', value: 'itemBaseAmount', width: '130px' },
        { text: '', value: 'obrisi', align: 'center', sortable: false, width: '48px' }
      ]
    } catch (error) {
      // eslint-disable-next-line
      console.log(error)
    }
  },
  methods: {
    sveRijeci,
    // Odabir sifre povlaci naziv, mjeru i zadnju nabavnu cijenu iz sifrarnika.
    artiklOdabran () {
      this.greske = []
      this.noviArtikl = this.novaSifra && this.novaSifra.itemID
        ? this.sifrarnik.find(a => a.itemID === this.novaSifra.itemID)
        : null
      if (this.noviArtikl) {
        this.novaCijena = parseFloat(this.noviArtikl.itemSuppBasePrice) ||
          parseFloat(this.noviArtikl.itemSuppPrice) || null
      }
    },
    dodaj () {
      const podaci = {
        skupina: MATERIJAL,
        glava: this.$store.state.documentActivHead || {},
        artikl: this.noviArtikl,
        kolicina: this.novaKolicina,
        cijena: this.novaCijena,
        databaseDocID: this.$store.state.databaseDocID,
        korisnik: this.$store.state.user ? this.$store.state.user.email : ''
      }
      this.greske = provjeri(podaci)
      if (this.greske.length) { return }

      const stavke = (this.$store.state.documentActivItems || []).slice()
      const nova = slozi(podaci)
      if (this.urediIndex >= 0) {
        // Izmjena zadrzava id postojece stavke, inace bi se pri spremanju
        // dokumenta upisala kao nova, a stara ostala.
        nova.id = stavke[this.urediIndex] ? stavke[this.urediIndex].id : undefined
        stavke.splice(this.urediIndex, 1, nova)
      } else {
        stavke.push(nova)
      }
      this.$store.dispatch('setDocumentActivItems', stavke)
      // Zaglavlje mora pratiti stavke, inace financijska kartica ostaje stara.
      const glava = Object.assign({}, this.$store.state.documentActivHead, zbrojNaloga(stavke))
      this.$store.dispatch('setDocumentActivHead', glava)

      this.ocisti()
      this.osvjezi()
      // Kursor natrag na sifru, da se sljedeca stavka unese bez misa.
      this.$nextTick(() => { if (this.$refs.sifra) { this.$refs.sifra.focus() } })
    },
    ocisti () {
      this.novaSifra = null
      this.noviArtikl = null
      this.novaKolicina = null
      this.novaCijena = null
      this.urediIndex = -1
      this.greske = []
    },
    odustani () {
      this.ocisti()
    },
    obrisi (item) {
      if (this.locked) { return }
      const opis = (item.itemID || '') + ' ' + (item.itemName || '')
      if (!confirm('Obrisati stavku ' + opis.trim() + '?')) { return }
      const stavke = (this.$store.state.documentActivItems || []).slice()
      const i = stavke.indexOf(item)
      if (i === -1) { return }
      stavke.splice(i, 1)
      this.$store.dispatch('setDocumentActivItems', stavke)
      const glava = Object.assign({}, this.$store.state.documentActivHead, zbrojNaloga(stavke))
      this.$store.dispatch('setDocumentActivHead', glava)
      // Ako se brisala bas stavka koja se uredivala, redak se mora isprazniti.
      if (this.urediIndex === i) { this.ocisti() }
      this.osvjezi()
    },
    osvjezi () {
      this.items = (this.$store.state.documentActivItems || []).filter(item => {
        return parseInt(item.FreeF6) === 3
      })
    },
    localMoney(moneyToFormat) {
      return moneyFormat(moneyToFormat)
    },
    // Klik na stavku puni gornji redak, umjesto da otvara zaseban ekran:
    // ispravak se radi na istom mjestu gdje se i unosi.
    navigateTo (item) {
      if (this.locked) { return }
      this.greske = []
      this.urediIndex = (this.$store.state.documentActivItems || []).indexOf(item)
      this.noviArtikl = this.sifrarnik.find(a => a.itemID === item.itemID) || {
        itemID: item.itemID, itemName: item.itemName, itemUnit: item.itemUnit,
        itemVAT1Percentage: item.itemVAT1Percentage
      }
      this.novaSifra = {itemID: item.itemID, itemByID: item.itemID + ' ' + (item.itemName || '')}
      this.novaKolicina = parseFloat(item.itemPcsMinus) || 0
      this.novaCijena = parseFloat(item.itemSuppBasePrice) || parseFloat(item.itemSuppPrice) || 0
    },
    newMaterial () {
      this.$store.dispatch('seteditItem', 0)
      this.$store.dispatch('settypePOS', 3)
      // warehouse minus
      this.$store.dispatch('setDocumentSide', 2) 
      this.$router.push({
        name: 'documentdetailcreateedit3'
      })
    }
  },
  watch: {

  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* Naslov skupine. "v-card-title" je flex spremnik, pa razmaci izmedu elemenata
   nestaju - svaki razmak mora doci iz margine, inace se tekst slijepi
   ("materijala- razduzuje skladiste14 stavaka").
   Boja NIJE jedini znak skupine: uz nju idu glif i rijec, pa se razlika vidi i
   na crno-bijelom ispisu. */
.naslov-kartice {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #1565c0;
  background: #eef4fb;
  border-bottom: 2px solid #1976d2;
  padding: 8px 14px;
  min-height: 0;
  flex-wrap: nowrap;
}
.naslov-kartice .glif { margin-right: 8px; font-size: 15px; line-height: 1; }
.naslov-kartice .smjer {
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0;
  padding: 2px 9px;
  border-radius: 10px;
  margin-left: 12px;
  white-space: nowrap;
}
.naslov-kartice .znak {
  font-size: 13px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  margin-left: 12px;
  white-space: nowrap;
}
.naslov-kartice .iznos {
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  margin-left: 12px;
}
.utroseno .naslov-kartice { color: #8a4b00; background: #fff8f0; border-bottom-color: #ef6c00; }
.utroseno .naslov-kartice .smjer { background: #ffe0b2; color: #8a4b00; }
.kol { font-weight: 600; }
.zbroj-red td {
  font-weight: 700;
  border-top: 2px solid #e0e0e0;
  background: #fafafa;
  font-variant-numeric: tabular-nums;
}
.rad .naslov-kartice { color: #4527a0; background: #f5f3fb; border-bottom-color: #5e35b1; }
.rad .naslov-kartice .smjer { background: #ede7f6; color: #4527a0; }
.ostalo .naslov-kartice { color: #0d47a1; background: #eef4fb; border-bottom-color: #1976d2; }
.ostalo .naslov-kartice .smjer { background: #e3f2fd; color: #0d47a1; }
.proizvedeno .naslov-kartice { color: #1b5e20; background: #f1f8f1; border-bottom-color: #43a047; }
.proizvedeno .naslov-kartice .smjer { background: #c8e6c9; color: #1b5e20; }
.unos {
  display: grid;
  grid-template-columns: 190px 1fr 70px 110px 130px 130px auto;
  gap: 8px;
  align-items: center;
  padding: 10px 14px;
  background: #fffaf4;
  border-bottom: 1px solid #e0e0e0;
}
.unos .polje-broj >>> input { text-align: right; }
.greska { padding: 6px 14px; color: #c62828; font-size: 13px; }
.unos .gumbi { display: flex; gap: 4px; align-items: center; }
@media (max-width: 1100px) {
  .unos { grid-template-columns: 1fr 1fr; }
}
</style>
